/**
 * Per-user chat stream timeout preferences.
 * Backed by the Supabase-synced chat preferences store (user_chat_preferences);
 * the legacy "chat_stream_preferences" localStorage key was migrated and removed.
 * Public API unchanged — callers keep working.
 */
import {
  getChatPreferencesSnapshot,
  updateChatPreferences,
} from "@/lib/chatPreferencesStore";

export type StreamTimeoutPreset = "5" | "10" | "15" | "30" | "custom" | "none";

export interface ChatStreamPreferences {
  timeoutPreset: StreamTimeoutPreset;
  /** Used when timeoutPreset is "custom". Clamped to 1–120 minutes. */
  customTimeoutMinutes: number;
}

const CUSTOM_MINUTES_MIN = 1;
const CUSTOM_MINUTES_MAX = 120;

function clampCustomMinutes(n: number): number {
  return Math.max(CUSTOM_MINUTES_MIN, Math.min(CUSTOM_MINUTES_MAX, Math.round(n)));
}

export function getChatStreamPreferences(): ChatStreamPreferences {
  const prefs = getChatPreferencesSnapshot();
  return {
    timeoutPreset: prefs.streamTimeoutPreset,
    customTimeoutMinutes: prefs.streamCustomTimeoutMinutes,
  };
}

export function setChatStreamPreferences(prefs: Partial<ChatStreamPreferences>): ChatStreamPreferences {
  const current = getChatStreamPreferences();
  const next: ChatStreamPreferences = {
    timeoutPreset: prefs.timeoutPreset ?? current.timeoutPreset,
    customTimeoutMinutes:
      prefs.customTimeoutMinutes != null
        ? clampCustomMinutes(prefs.customTimeoutMinutes)
        : current.customTimeoutMinutes,
  };
  updateChatPreferences({
    streamTimeoutPreset: next.timeoutPreset,
    streamCustomTimeoutMinutes: next.customTimeoutMinutes,
  });
  return next;
}

/** Effective client stream timeout in ms. 0 = no limit. */
export function getEffectiveStreamTimeoutMs(): number {
  const { timeoutPreset, customTimeoutMinutes } = getChatStreamPreferences();
  if (timeoutPreset === "none") return 0;
  if (timeoutPreset === "custom") return customTimeoutMinutes * 60_000;
  return Number(timeoutPreset) * 60_000;
}

export function getStreamTimeoutLabel(): string {
  const { timeoutPreset, customTimeoutMinutes } = getChatStreamPreferences();
  if (timeoutPreset === "none") return "No limit";
  if (timeoutPreset === "custom") return `${customTimeoutMinutes} min`;
  return `${timeoutPreset} min`;
}
